import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import cuid from "cuid";
import ReactCodeInput from "react-code-input";
import moment from "moment";
import {
  userSignIn,
  sendAuthPin,
  fetchAuthPin,
  setLoginAttemptModal,
} from "actions/Auth";
import {
  setLoginPerformance,
  submitNotificationPerformance,
  setErrorDetails,
  // setGeneralLoader
} from "actions/App";
import { useModal } from "context/ModalContext";
import { toastManager } from "components/ToastNotification";
import ButtonContained from "components/ButtonContained";
import ButtonUncontained from "components/ButtonUncontained";
import TextInput from "components/TextInput";

const SignIn = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const { openModal, closeModal } = useModal();
  const countryProp = useSelector((state) => state.app.country);
  const loginPerformance = useSelector((state) => state.app.loginPerformance);
  const showPin = useSelector((state) => state.auth.showPin);
  const loginConfirm = useSelector((state) => state.auth.loginConfirm);
  const invalidLoginModal = useSelector((state) => state.auth.invalidLoginModal);
  const errorDetails = useSelector((state) => state.app.errorDetails);

  const nseData = localStorage.getItem("nse_login_obj");
  const parsedData = nseData ? JSON.parse(nseData) : {};
  const phoneLogin = parsedData.phone ? true : false;
  const phone = parsedData.phone || "";
  const email = parsedData.email || "";

  const [country, setCountry] = useState(countryProp);
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [inputCode, setInputCode] = useState("");
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    document.title = "Login | Smart Entry";
  }, []);

  useEffect(() => {
    if (showPin) {
      openModal(
        <>
          <div className="px-10 py-4" onKeyDown={handleEnter}>
            <p className="tw-paragraph-reg mb-2">
              {intl.formatMessage({ id: "form.deviceCode" })}:
            </p>
            <ReactCodeInput
              id="Login-Page-Pin-Code-Input"
              data-cy="Login-Page-Pin-Code-Input"
              fields={5}
              inputMode="numeric"
              onChange={(value) => setInputCode(value)}
              value={inputCode}
              inputStyle={{
                outline: "none",
                fontFamily: "Outfit",
                fontSize: "16px",
                fontWeight: "bold",
                marginRight: 8,
                textAlign: "center",
                border: "1px solid #999999",
                borderRadius: 4,
                color: "black",
                height: "40px",
                width: "40px"
              }}
            />
          </div>
          <div className="flex justify-end gap-2 px-10">
            <ButtonContained
              id="Password-Login-2-Factor-Resend-Button"
              dataCy="Password-Login-2-Factor-Resend-Button"
              text={intl.formatMessage({id: "form.resendPin"})}
              type="default"
              onClick={newPin}
            />
            <ButtonContained
              id="Password-Login-2-Factor-Submit-Button"
              dataCy="Password-Login-2-Factor-Submit-Button"
              text={intl.formatMessage({id: "comp.button.submit"})}
              type="default"
              onClick={submitCode}
            />
          </div>
        </>,
        intl.formatMessage({ id: "form.device2FA" })
      )
    }
  }, [showPin])

  useEffect(() => {
    if (invalidLoginModal) {
      openModal(
        <>
          <div className="px-10 py-4">
            <p className="tw-paragraph-reg mb-2">
              {intl.formatMessage({ id: "form.login.visitKnowledgeBase" })}:
            </p>
          </div>

          <div className="flex justify-end gap-2 px-10">
            <ButtonContained
              id="Password-Login-2-Factor-Resend-Button"
              dataCy="Password-Login-2-Factor-Resend-Button"
              text={intl.formatMessage({id: "pages.users.dismissTraining"})}
              type="default"
              onClick={() => dispatch(setLoginAttemptModal(false))}
            />
            <ButtonContained
              id="Password-Login-2-Factor-Submit-Button"
              dataCy="Password-Login-2-Factor-Submit-Button"
              text={intl.formatMessage({id: "form.login.knowledgeBase"})}
              type="default"
              onClick={viewKnowledgeBase}
            />
          </div>
        </>,
        intl.formatMessage({ id: "form.login.issues" })
      )
    }
  }, [invalidLoginModal])

  useEffect(() => {
    return () => {
      // Runs on unmount
      dispatch(setErrorDetails(null));
    };
  }, []);

  useEffect(() => {
    setCountry(countryProp);
  }, [countryProp]);

  useEffect(() => {
    if (errorDetails) {
      setHasError(true);
      setErrorMessage(errorDetails.description);
    } else {
      setHasError(false);
      setErrorMessage("");
    }
  }, [errorDetails]);

  const validate = (e) => {
    e.preventDefault();

    if (password.trim() === "") {
      setHasError(true);
      setErrorMessage(intl.formatMessage({id: "notifications.error.blank.password"}));
      return;
    }
    login(e)
  };
  const login = (e) => {
    e.preventDefault();

    // dispatch(setGeneralLoader({loading: true, text: intl.formatMessage({ id: "pages.appInstructions.loggingIn" })}))

    const sessionConfirm = JSON.parse(sessionStorage.getItem("login_confirm"));
    const localConfirm = JSON.parse(localStorage.getItem("login_confirm"));
    const confirm = sessionConfirm || loginConfirm || localConfirm;

    if (confirm) {
      dispatch(
        setLoginPerformance({
          ...loginPerformance,
          start_time: moment(),
          loginType: confirm.phone ? "Phone" : "Email",
          emailOrPhone: confirm.phone || confirm.email,
        })
      );
      dispatch(
        userSignIn({
          country,
          phone: confirm.phone,
          email: confirm.email,
          password,
          phoneLogin: confirm.phone ? true : false,
          countryCode: confirm.countryCode,
          deviceUUID: confirm.deviceUUID,
        })
      );
    } else {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.fail"}));
      history.push("/signin");
    }
  };

  const submitCode = () => {
    const deviceUUID = cuid();
    dispatch(
      fetchAuthPin({
        pin: inputCode,
        login: {
          country,
          phone,
          email,
          password,
          phoneLogin,
          countryCode: country.phone[0].toString(),
          deviceUUID,
        },
      })
    );
    closeModal();
  };

  const newPin = () => {
    dispatch(
      submitNotificationPerformance({
        notification_id: phoneLogin ? phone : email,
        notification_type: "password_reset",
        notification_name: "2_factor_pin",
        start_time: moment().utc(),
      })
    );
    dispatch(sendAuthPin());
    closeModal();
  };

  const handleEnter = (e) => {
    if (e.keyCode === 13) {
      submitCode();
    }
  };

  const clearError = () => {
    setHasError(false);
    setErrorMessage("");
  }

  const viewKnowledgeBase = () => {
    window.location = 'https://www.janusintl.com/knowledge';
  };

  return (
    <div className="h-full flex flex-col">
      <label className="flex justify-between items-center h-10">
        <h5 className="tw-heading-5 font-bold text-black">
          {intl.formatMessage({ id: "form.password" })}
        </h5>
      </label>

      <div className="flex flex-col justify-between">
        <form>
          <div className="mt-6 mb-16 w-full">
            <TextInput
              id="Password-Login-Password-Input"
              dataCy="Password-Login-Password-Input"
              type={reveal ? "text" : "password"}
              name={reveal ? "text" : "password"}
              placeholder={`${intl.formatMessage({id: "form.password"})}`}
              onChange={(e) => {
                if (hasError) {
                  clearError();
                }
                setPassword(e.target.value)
              }}
              value={password}
              buttonOneIcon="close"
              buttonOneAction={(e) => {
                e.preventDefault();
                setPassword("")
              }}
              buttonTwoIcon={reveal ? "eye" : "eyeOff"}
              buttonTwoAction={(e) => {
                e.preventDefault();
                setReveal(!reveal)
              }}
              hasError={hasError ? true : false}
              errorMessage={hasError ? errorMessage : null}
            />
          </div>
          <div className="flex justify-between">
            <ButtonUncontained
              id="Password-Login-Forgot-Password-Button"
              dataCy="Password-Login-Forgot-Password-Button"
              text={intl.formatMessage({id: "comp.button.forgotPassword"})}
              type="alt"
              onClick={(e) => {
                e.preventDefault();
                history.push("/signin/forgot")
              }}
            />
            <ButtonContained
              id="Password-Login-Login-Button"
              dataCy="Password-Login-Login-Button"
              text={intl.formatMessage({id: "comp.button.login"})}
              type="default"
              buttonType="submit"
              onClick={(e) => {validate(e)}}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignIn;